import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/domains/general/api/client';

import { adminKeys } from './keys';
import type {
  AddUserPerspectiveInput,
  OkResponse,
  RemoveUserPerspectiveInput,
  UpdateUserRoleInput,
  UpsertUserAssignmentInput,
  UpsertUserAssignmentResponse,
} from './types';

export function useUpsertUserAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertUserAssignmentInput) =>
      apiFetch<UpsertUserAssignmentResponse>('/admin/users', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: UpdateUserRoleInput) =>
      apiFetch<{ userId: string; role: string }>(`/admin/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<OkResponse>(`/admin/users/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useAddUserPerspective() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, perspectiveId }: AddUserPerspectiveInput) =>
      apiFetch<OkResponse>(`/admin/users/${userId}/perspectives`, {
        method: 'POST',
        body: JSON.stringify({ perspectiveId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useRemoveUserPerspective() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, perspectiveId }: RemoveUserPerspectiveInput) =>
      apiFetch<OkResponse>(
        `/admin/users/${userId}/perspectives/${perspectiveId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}
